<template>
  <div id="articleCreationContainer">
    <div
      v-if="preparingAi || preparingAiLocal"
      class="d-flex justify-center my-16"
    >
      <AISparkline class="article-content" />
    </div>
    <div v-else>
      <PartsGenerateStatus
        v-if="article && (isAnalyze || article.aiGenerateMode === 'whole')"
        :enhanced="article.enhanced"
        :ai-generate-status="article.aiGenerateStatus"
        :mode="isAnalyze ? 'parts' : 'whole'"
        @retry="onPartsRetry"
      />
      <div
        v-if="isAnalyze && article && article.blockProposals && article.blockProposals.length && !sharePreview"
        class="d-flex justify-center"
        data-test="block-proposals-container"
      >
        <BlockProposals
          :proposals="article.blockProposals"
          @choose="chooseProposal"
          @retry="retryFailedProposal"
        />
      </div>
      <div
        v-if="!sharePreview && articleEnhancePossible && !isAnalyze"
        class="enhance-banner-slot mt-4"
      >
        <div class="d-flex justify-center">
          <ArticleEnhancingAlert
            v-if="articleEnhancePossible && !isAnalyze"
            :enhancing="enhancing"
            @click="manualEnhance"
          />
        </div>
      </div>
      <ArticleNavigationChips
        :is-analyze="isAnalyze"
        :class="{ 'editorsBuffer' : !showEditors }"
      />
      <div
        v-if="showEditors"
        class="d-flex justify-center"
      >
        <div class="article-content">
          <LucyNudge v-if="!sharePreview" />
        </div>
      </div>
      <div
        v-for="{number} of article.getAllParts()"
        v-show="showEditors"
        id="partEditorContainer"
        :key="'e' + number"
      >
        <ArticleContentPartEditors
          class="mt-4"
          :part="'h' + number"
          :words-to-highlight="wordsToHighlight"
          :inspirations="inspirations"
          :loading-inspirations="loadingSERP || loadingInspirations"
          :disabled="!articleContentReady || sharePreview"
        />
        <ContentList v-if="number === 1" />
      </div>
      <ArticleQuestionEditors
        v-if="showEditors"
        class="mt-4"
        :disabled="!articleContentReady || sharePreview"
        :words-to-highlight="wordsToHighlight"
      />

      <!-- Owner must edit E-E-A-T after scrape. Do not bind articleContentReady. -->
      <ArticleEatAttribution
        v-if="showEditors"
        :loading="eatLoading"
      />

      <div
        v-if="showEditors"
        id="serpSnippet"
      >
        <div class="d-flex justify-center">
          <div class="article-content d-flex align-center justify-space-between px-1 mb-n2">
            <span class="text-caption-primary font-weight-medium">{{ $t('seoScore.serpIsComplete.label') }}</span>
            <LucySpark metric="serpIsComplete" />
          </div>
        </div>
        <ArticleMetaPartEditor
          v-for="[part, config] of Object.entries(ArticleMetasConfig)"
          :key="part"
          class="mt-2"
          :part="part"
          v-bind="config"
          :words-to-highlight="wordsToHighlight"
          :inspirations="inspirations"
          :loading-inspirations="loadingSERP || loadingInspirations"
          :disabled="!articleContentReady || sharePreview"
        />
        <ArticleSERPPreview
          :meta-title="article.metaTitle"
          :meta-description="article.metaDescription"
          :keyword="selectedKeyword"
          :words-to-highlight="wordsToHighlight"
          class="mb-8"
        />
      </div>
      <ArticleImagesTable
        v-if="showEditors"
        :words-to-highlight="wordsToHighlight"
      />
      <ArticleURLSuggestion
        :words-to-highlight="wordsToHighlightNoDiacritics"
        class="mb-8"
      />
    </div>
    <div v-if="!sharePreview" class="d-flex justify-center">
      <div class="article-content">
        <ArticleSaveButton
          class="ma-2"
          :disabled="disableSave"
          @input="saveArticle"
        />
        <ArticlePreview class="mx-2" />
        <v-divider class="my-4" />
        <ArticleExport :recommended-length="recommendedWordCount" />
        <ArticleRecommendation
          id="recommendation"
          :serp-links="SERPLinks"
          class="mt-4"
        />
        <div class="workspace-inspiration-actions">
          <ArticleNewInspirationButton match-drawer-width />
          <ArticleNewInspirationButton
            is-analyze
            match-drawer-width
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import ArticleRecommendation from '@/components/article/ArticleRecommendation.vue';
    import {debounce, DebouncedFunc} from 'lodash-es';
    import {removeDiacritics, stripMarkers, stripTagsAndEntities} from '@/utils/string';
    import {Functions} from '@/plugins/Functions';
    import {ArticleMetasConfig} from '@/config/ArticleConfig';
    import {
        Article,
        ArticleContentKey,
        ArticleContentTag,
        ArticleInspirations,
        ArticlePartContent,
        ArticleQuestion,
        ArticleWordCounts,
        Keyword
    } from "@/model/Article";
    import ArticleMetaPartEditor from "@/components/article/ArticleMetaPartEditor.vue";
    import ArticleContentPartEditors from "@/components/article/ArticleContentPartEditors.vue";
    import ArticleSERPPreview from "@/components/article/ArticleSERPPreview.vue";
    import ArticleQuestionEditors from "@/components/article/ArticleQuestionsEditors.vue";
    import {EventBus} from "@/utils/EventBus";
    import ContentList from "@/components/article/ContentList.vue";
    import ArticleExport from "@/components/article/ArticleExport.vue";
    import ArticlePreview from "@/components/article/ArticlePreview.vue";
    import {captureException} from "@/plugins/Sentry";
    import {ScrapperResponse} from "@/model/Scrapper";
    import {
        isAnalyzeScrapeExtractionFailed,
        isAnalyzeScrapeExtractionPartial,
    } from '@/service/analyzeScrapeOutcome';
    import {Firestore} from "@/plugins/Firestore";
    import ArticleNavigationChips from "@/components/article/ArticleNavigationChips.vue";
    import {clearAiContentWatchers, rearmGenerationStatusWatch, shouldSkipAutoPolish, unlockFilledAndOrphanSlots, watchForAi} from "@/service/articleAiContent";
    import {adoptRunInProgress, beginPartsRetry, markPartsRunStarted, retryFailedBlock, settleProcessingParts} from '@/service/partsTerminalApply';
    import {startGenerationRun, type GenerationRunStart} from '@/service/generationStart';
    import {PARTS_GENERATE_DEADLINE_MS, generationAlreadyRunning, partsRetryVisible} from '@/service/partsTerminalState';
    import {decideUrlImproveEnqueue, onUrlImproveGate, readUrlImproveGate} from '@/service/urlImproveStart';
    import {suggestedH2UrlAction} from '@/service/analyzeFidelity';
    import {articleHasScrapedHeadings, enhanceWaitingForExtraction, readChatWritePath} from '@/utils/articleWriteCta';
    import PartsGenerateStatus from '@/components/article/PartsGenerateStatus.vue';
    import BlockProposals from '@/components/article/BlockProposals.vue';
    import {applyBlockProposalChoice, hollowReviewFailure, settledGenerationStatus, type ProposalChoice} from '@/service/blockProposals';
    import {clientEnhanceRequest} from '@/service/clientEnhance';
    import {syncSelectedBriefIntoKeywords} from '@/service/briefEditorActions';
    import AISparkline from "@/components/ai/AISparkline.vue";
    import ArticleNewInspirationButton from "@/components/article/ArticleNewInspirationButton.vue";
    import ArticleSaveButton from "@/components/article/ArticleSaveButton.vue";
    import {geometricAverage, inspirationWordLength} from "@/utils/ArticleComputed";
    import {seoScoreContext, SeoScoreContext} from "@/model/SeoScore";
    import ArticleURLSuggestion from "@/components/article/ArticleURLSuggestion.vue";
    import ArticleImagesTable from "@/components/article/ArticleImagesTable.vue";
    import ArticleEatAttribution from "@/components/article/ArticleEatAttribution.vue";
    import {loadEatAttributionForUrl} from '@/service/articleEatFromUrl';
    import {hasEatAuthor} from '@/service/extractEatAttribution';
    import {Toast} from '@/model/Toast';
    import {getBaseUrl, FALLBACK_URL, getSuggestedUrl} from '@/utils/ArticleUrl';
    import ArticleEnhancingAlert from '@/components/article/ArticleEnhancingAlert.vue';
    import UserPlanMixin from '@/mixins/UserPlanMixin';
    import {articlePositionSources} from "@/utils/Article";
    import {shouldSeedEmptyFaqRow} from '@/service/articleFaqFromUrl';
    import {densityListsEqual, syncKeywordDensityList} from '@/service/keywordInflection';
    import {resolveArticleMainKeyword} from '@/service/articleMainKeyword';
    import {collapseAnalysisAndScrollToHBlocks} from '@/service/articleWriteWorkspace';
    import {countIncompleteFaqRows, isCompleteFaqPair} from '@/service/articleSanitize';
    import {runWhenPlanLoaded} from '@/service/articleLimitGate';
    import {countStemKeywordHitsForPhrases, findStemTokenRanges} from '@/service/keywordStemDensity';
    import {isKeywordDensityEditing} from '@/service/keywordDensityEdit';
    import {EDITOR_INPUT_DEBOUNCE_MS} from '@/service/editorInputLatency';
    import {
        isLocalPreviewAi,
        raceWithLocalAiTimeout,
        unlockLocalArticleEditors,
    } from '@/service/aiWaitFallback';
    import LucySpark from '@/components/lucy/LucySpark.vue';
    import LucyNudge from '@/components/lucy/LucyNudge.vue';
    import {applyScrapedCoverage} from '@/service/aiWritingBrief';
    import {dismissSaveHeldToasts} from '@/service/saveHeldToast';

    export default Vue.extend({
        name: 'ArticleCreation',
        components: {
            ArticleSaveButton, ArticleNewInspirationButton, AISparkline, ArticleNavigationChips, ArticlePreview,
            ArticleExport, ArticleQuestionEditors, ArticleSERPPreview, ArticleURLSuggestion,
            ArticleContentPartEditors, ArticleMetaPartEditor, ArticleRecommendation, ContentList, ArticleImagesTable,
            ArticleEnhancingAlert, ArticleEatAttribution, LucySpark, LucyNudge, PartsGenerateStatus,
            BlockProposals
        },
        mixins: [UserPlanMixin],
        // emits: ['save'],
        props: {
            preparingAi: {
                type: Boolean,
                default: false
            },
            seoScoreContext: {
                type: Object as PropType<SeoScoreContext>,
                default: () => seoScoreContext
            },
            isAnalyze: {
                type: Boolean,
                default: false
            },
            disableSave: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            loadingSERP: true,
            SERPLinks: [] as { url: string, title: string }[],
            ArticleMetasConfig: ArticleMetasConfig,
            preparingAiLocal: false,
            mergedText: '',
            wordsToHighlight: {} as { [key: string]: string[] }, /* TODO this is just a permutation, refactor */
            inspirations: {} as ArticleInspirations,
            loadingInspirations: true,
            articleWordCount: 0,
            AIRegenerateInterval: null as any,
            articlePolishPossible: false,
            polishingToastId: null as number | null,
            generatingToastId: null as number | null,
            enhancingToastId: null as number | null,
            isNew: false,
            articleUrlsAdded: false,
            suggestedUrlWarned: false,
            articleEnhancePossible: false,
            localAiTimer: null as ReturnType<typeof setTimeout> | null,
            partsDeadlineTimer: null as ReturnType<typeof setTimeout> | null,
            unsubscribeUrlImproveGate: null as null | (() => void),
            localAiUnlocked: false,
            eatLoading: false,
            generationUiFinished: false
        }),
        computed: {
            articleId: function (): string {
                return this.$route.params.articleId as string;
            },
            article: function (): Article {
                return this.$store.getters.article;
            },
            selectedKeyword: function (): string {
                return this.article.selectedKeyword;
            },
            debouncedMergeText: function (): DebouncedFunc<() => void> {
                // Trailing only. leading:true ran the full stem scan on the first key.
                return debounce(() => {
                    this.mergeText();
                }, EDITOR_INPUT_DEBOUNCE_MS);
            },
            validInspirations: function (): ScrapperResponse[] {
                return Object.values(this.inspirations)
                    .filter(inspiration => inspiration.status === 200);
            },
            validKeywords: function (): string[] {
                return this.article.allKeywords
                    .map((word: Keyword) => word.keyword)
                    .filter((word: string) => word && word.trim());
            },
            wordsToCountIntoDensity: function (): string[] {
                return this.article.allKeywords
                    .filter((word: Keyword) => word.checked)
                    .map((word: Keyword) => word.keyword)
                    .filter((word: string) => word && word.trim());
            },
            articleDensity: function (): number {
                if (this.wordsToCountIntoDensity.length) {
                    const language = this.article.language_code || 'cs';
                    const counts = countStemKeywordHitsForPhrases(
                        this.mergedText,
                        this.wordsToCountIntoDensity,
                        language
                    );
                    const occurrences = this.wordsToCountIntoDensity
                        .reduce((acc: number, word: string) => acc + (counts[word] || 0), 0);
                    return (occurrences / (this.articleWordCount || 1)) * 100;
                } else {
                    return 0;
                }
            },
            topTenGeometricAverageWordCount: function (): number | null {
                if (this.loadingInspirations) {
                    return null;
                } else {
                    return geometricAverage(this.validInspirations.map(inspirationWordLength));
                }
            },
            topTenLongestWordCount: function (): number | null {
                if (this.loadingInspirations) {
                    return null;
                } else {
                    return Math.max(...this.validInspirations.map(inspirationWordLength));
                }
            },
            recommendedWordCount: function (): number {
                return this.topTenGeometricAverageWordCount || 1200;
            },
            showEditors: function (): boolean {
                return !this.isAnalyze || !this.$store.getters.articleAnalyzeScrapeLoading;
            },
            loadingCompleted: function (): boolean {
                return this.$store.getters.articleAiLoading.loadingCompleted;
            },
            polishing: {
                get: function (): boolean {
                    return this.$store.getters.articleAiPolishing;
                },
                set: function (newValue: boolean): void {
                    this.$store.commit('setArticleAiPolishing', newValue);
                }
            },
            enhancing: {
                get: function (): boolean {
                    return this.$store.getters.articleAiEnhancing;
                },
                set: function (newValue: boolean): void {
                    this.$store.commit('setArticleAiEnhancing', newValue);
                }
            },
            articleContentReady: function (): boolean {
                return this.$store.getters.articleContentReady;
            },
            firstH2Paragraph: function (): string {
                const block = this.article && this.article.h2 && this.article.h2[0];
                return block && block.p ? block.p : '';
            },
            articleSaving: function (): boolean {
                return this.$store.getters.articleSaving;
            },
            sharePreview(): boolean {
                return !!this.$store.getters.sharePreview;
            },
            articleSeoScoreChecked: function (): boolean {
                return this.$store.getters.articleSeoScoreChecked;
            },
            articlePolishPossibleToDo: function (): boolean {
                // Polish-as-step-2 is gone: whole-article generate already wrote the text.
                return false;
            },
            wordsToHighlightNoDiacritics: function () {
                // for suggested URL slug we need to remove diacritics for highlighting
                const normalized: { [key: string]: string[] } = {};
                for (const [key, values] of Object.entries(this.wordsToHighlight)) {
                    normalized[key] = values.map(value => {
                        return removeDiacritics(value);
                    });
                }
                return normalized;
            },
            articleGenerationPossibleToDo: function (): boolean {
                return !this.articleSaving &&
                    this.articleSeoScoreChecked &&
                    !this.$store.getters.articleAnalyzeScrapeLoading &&
                    this.article.isAnalyze && this.article.enhanced !== 'done';
            }
        },
        watch: {
            article: {
                immediate: true,
                // Not deep: body typing used to retrigger keyword sync + permute +
                // stem-density for every character (100+ keywords → multi-second lag).
                handler: function (): void {
                    if (this.article && !this.article.allKeywords) {
                        this.article.allKeywords = [];
                    }
                    this.syncInflectedKeywords();
                    this.updateWordsToHighlight();
                    this.debouncedMergeText();
                }
            },
            /** SeoScore context watchers **/
            articleDensity: {
                immediate: true,
                handler: function (value): void {
                    // eslint-disable-next-line vue/no-mutating-props
                    this.seoScoreContext.articleDensity = value;
                }
            },
            topTenGeometricAverageWordCount: {
                immediate: true,
                handler: function (value): void {
                    // eslint-disable-next-line vue/no-mutating-props
                    this.seoScoreContext.topTenGeometricAverageWordCount = value;
                }
            },
            topTenLongestWordCount: {
                immediate: true,
                handler: function (value): void {
                    // eslint-disable-next-line vue/no-mutating-props
                    this.seoScoreContext.topTenLongestWordCount = value;
                }
            },
            mergedText: {
                immediate: true,
                handler: function (value): void {
                    // eslint-disable-next-line vue/no-mutating-props
                    this.seoScoreContext.mergedText = value;
                }
            },
            /** End SeoScore context watchers **/
            'article.selectedKeyword': {
                immediate: true,
                handler: function () {
                    this.syncInflectedKeywords();
                    this.updateWordsToHighlight();
                    if (this.sharePreview) {
                        return;
                    }
                    if (this.article.selectedKeyword) {
                        this.loadingSERP = true;
                        Functions.D4SGetSERP({
                            keyword: this.article.selectedKeyword,
                            location_code: this.article.location_code,
                            language_code: this.article.language_code
                        }).then(response => {
                            this.SERPLinks = response as { url: string, title: string }[];
                            this.scrapUrls(this.SERPLinks.map(link => link.url));
                        }).catch(error => {
                            window.console.warn('SERP fetch failed', error);
                        }).finally(() => {
                            this.loadingSERP = false;
                        });
                    }
                }
            },
            'article.secondaryKeywords': {
                handler: function (): void {
                    if (isKeywordDensityEditing()) {
                        return;
                    }
                    this.syncInflectedKeywords();
                    this.updateWordsToHighlight();
                }
            },
            'article.allKeywords': {
                deep: true,
                handler: function (): void {
                    if (isKeywordDensityEditing()) {
                        return;
                    }
                    this.syncInflectedKeywords();
                    this.updateWordsToHighlight();
                    // Keyword Enter/+/confirm is a single commit — recalc density now.
                    this.mergeText();
                }
            },
            '$store.getters.articleWordCount': {
                deep: true,
                immediate: true,
                handler: function (newValue: ArticleWordCounts): void {
                    this.articleWordCount = newValue.getSum();
                    // CKEditor already trailing-debounced the Vuex write; refresh
                    // density now so the pie lands ~400ms after the last key.
                    this.mergeText();
                }
            },
            '$store.getters.articleAiLoading': {
                deep: true,
                immediate: true,
                handler: function (): void {
                    this.$store.getters.articleAiLoading.computeLoadingRatio();
                    if (this.sharePreview) {
                        return;
                    }
                    unlockFilledAndOrphanSlots();
                    // Generate seeds one empty FAQ row after AI question loading finishes.
                    // URL-improve keeps the scrape: no empty placeholder row.
                    if (shouldSeedEmptyFaqRow({
                        isAnalyze: this.isAnalyze,
                        questionCount: this.$store.getters.article.questions.length,
                        questionsLoading: this.$store.getters.articleAiLoading.loadingRatioQuestions !== 0,
                    })) {
                        this.$store.commit('insertArticleQuestion', {
                            index: 0,
                            aiLoading: false
                        });
                    }
                }
            },
            articlePolishPossibleToDo: {
                immediate: true,
                handler: function (): void {
                    // Never enqueue OpenAIArticlePolish after generate. URL-analyze
                    // "Vylepšit článek" is enhance (step 1), not this rewrite.
                    if (shouldSkipAutoPolish(this.article) && (
                        this.article.polish === 'pending' || this.article.polish === 'processing'
                    )) {
                        this.$store.commit('setArticlePolish', 'skipped');
                    }
                    this.polishing = false;
                    this.articlePolishPossible = false;
                }
            },
            polishing: {
                immediate: true,
                handler: function (val: boolean): void {
                    // Polish-as-step-2 no longer locks the editor after generate.
                    if (val) {
                        this.$store.commit('setArticleAiPolishing', false);
                    }
                    if (this.polishingToastId) {
                        this.$store.dispatch('toasts/remove', this.polishingToastId);
                        this.polishingToastId = null;
                    }
                }
            },
            loadingCompleted: {
                immediate: true,
                handler: function (val: boolean): void {
                    if (!val || this.sharePreview) {
                        return;
                    }
                    const aiFailed: boolean = this.$store.getters.articleAiFailed;
                    if (aiFailed && this.generatingToastId) {
                        this.$store.dispatch('toasts/replace', {
                            id: this.generatingToastId,
                            type: 'error',
                            title: 'article.generateError.title',
                            content: 'article.generateError.content',
                            dismissible: true,
                        } as Toast);
                        this.clearLocalAiTimer();
                    }
                    if (aiFailed && this.enhancingToastId && !this.localAiUnlocked) {
                        // back to pending so the user can run the enhancement again, empty blocks get reused
                        this.$set(this.article, 'aiGenerateStatus', 'failed');
                        this.releaseAiUiLock('enhance', 'error');
                        void Firestore.updateArticleGenerationStatus(this.article, {
                            enhanced: 'pending',
                            aiGenerateStatus: 'failed',
                        });
                        return;
                    }
                    if (!aiFailed && this.shouldConfirmGeneration()) {
                        void this.confirmGenerationSave();
                    }
                }
            },
            articleContentReady: {
                handler: function (val: boolean): void {
                    if (!val) {
                        return;
                    }
                    if (this.shouldConfirmGeneration()) {
                        void this.confirmGenerationSave();
                        return;
                    }
                    this.addUrlsToArticle();
                }
            },
            loadingSERP: {
                handler: function (val: boolean): void {
                    if (!val) {
                        this.addUrlsToArticle();
                    }
                }
            },
            firstH2Paragraph: function (paragraph: string): void {
                if (this.isNew && !this.articleUrlsAdded && paragraph) {
                    this.addUrlsToArticle(true);
                }
            },
            articleGenerationPossibleToDo: function (val: boolean) {
                if (this.sharePreview || !this.article.id || !val) {
                    return;
                }

                this.articleEnhancePossible = true;
            },
            '$store.getters.articleAnalyzeScrapeLoading': function (): void {
                this.syncPendingUrlImprove();
            },
        },
        created: function (): void {
            EventBus.$on('generation-applied', this.onGenerationApplied);
            EventBus.$on('proposals-ready', this.onProposalsReady);
            EventBus.$on('parts-generation-failed', this.onPartsGenerationFailed);
            if (this.sharePreview) {
                return;
            }
            if (this.isAnalyze && !this.article.metaTitle && !this.article.forcedEmpty) {
                this.$store.commit('setArticleAnalyzeScrapeLoading', true);
                // if extraction is partial or failed, parent component will handle user decision how to proceed
                Functions.GetScrapped({site: this.article.url, skipCache: true})
                    .then(response => {
                        if (isAnalyzeScrapeExtractionFailed(response)) {
                            window.console.warn('Extraction failed');
                            this.$store.commit('setArticleAnalyzeScrapeLoading', false);
                            this.$emit('extractionFailed');
                            return;
                        }
                        if (isAnalyzeScrapeExtractionPartial(response)) {
                            this.$store.commit('setArticleAnalyzeScrapeLoading', false);
                            this.$emit('extractionPartial', response);
                            return;
                        }
                        // setArticleScrapedContent also applies the FAQ from the scrape.
                        this.$store.dispatch('setArticleScrapedContent', response)
                            .then(() => this.importEatAttribution(response))
                            .then(() => {
                                Object.values(articlePositionSources).forEach(source => {
                                    source.fetch(this.article.id as string).catch((error) => {
                                        window.console.warn('Position fetch failed', error);
                                    });
                                });
                                this.$store.commit('setArticleAnalyzeScrapeLoading', false);
                            });
                    })
                    .catch((error) => {
                        // firebase library failed for some reason
                        window.console.error(error);
                        this.$store.commit('setArticleAnalyzeScrapeLoading', false);
                        this.$emit('extractionFailed');
                    });
                this.isNew = true;
            } else if (!this.isAnalyze && this.article.loadedSeoScore === 0) {
                this.addGeneratingToast();
                if (this.article.ai_content_last_generated_at &&
                    (this.article.ai_content_last_generated_at + 1000 * 60 * 5) < Date.now()
                ) { // Older than 10 minutes
                    setTimeout(this.reGenerateAiContent, 1000 * 30); // Wait for AI progress loaded
                }
                this.preparingAiLocal = true;
                watchForAi().then(() => {
                    this.preparingAiLocal = false;
                    if (isLocalPreviewAi() && this.loadingCompleted) {
                        this.releaseAiUiLock('generate', 'local');
                    }
                });
                this.armLocalAiTimeout('generate');
                this.AIRegenerateInterval = setInterval(this.reGenerateAiContent, 1000 * 60 * 5); // Every 5 minutes
                this.isNew = true;
            } else if (this.article.enhanced === 'processing') {
                // A URL-improve run, an enhance of a finished article, or a v1.14.2
                // enhance that never ended (processing without a status).
                // refreshPartsTerminal settles the last one to failed with Retry.
                if (isLocalPreviewAi()) {
                    this.enhancing = false;
                    this.$set(this.article, 'enhanced', 'pending');
                    this.articleEnhancePossible = true;
                    this.localAiUnlocked = true;
                    unlockLocalArticleEditors();
                } else {
                    void this.refreshPartsTerminal(true);
                }
            }

            this.importEatAttribution();

            EventBus.$on('save', (callback = null) => {
                this.saveArticle(callback);
            });
            EventBus.$on('request-enhance', this.manualEnhance);
            this.unsubscribeUrlImproveGate = onUrlImproveGate(() => this.syncPendingUrlImprove());
            this.syncPendingUrlImprove();
        },
        mounted: function (): void {
            document.addEventListener("copy", this.copyListener);
            if (isLocalPreviewAi() && (this.enhancing || this.article.enhanced === 'processing' || this.enhancingToastId)) {
                this.localAiUnlocked = false;
                this.releaseAiUiLock('enhance', 'local');
            }
        },
        destroyed: function (): void { // TODO should be beforeUnmount but TypeScript definition does not know it...
            this.AIRegenerateInterval && clearInterval(this.AIRegenerateInterval);
            this.clearLocalAiTimer();
            this.clearPartsDeadline();
            this.dropUrlImproveGate();
            this.debouncedMergeText.cancel();
            clearAiContentWatchers();
            document.removeEventListener("copy", this.copyListener);
            EventBus.$off('save');
            EventBus.$off('request-enhance', this.manualEnhance);
            EventBus.$off('generation-applied', this.onGenerationApplied);
            EventBus.$off('proposals-ready', this.onProposalsReady);
            EventBus.$off('parts-generation-failed', this.onPartsGenerationFailed);
            if (this.polishingToastId) {
                this.$store.dispatch('toasts/remove', this.polishingToastId);
            }
            if (this.generatingToastId) {
                this.$store.dispatch('toasts/remove', this.generatingToastId);
            }
            if (this.enhancingToastId) {
                this.$store.dispatch('toasts/remove', this.enhancingToastId);
            }
        },
        beforeDestroy: function () {
            this.dropUrlImproveGate();
        },
        deactivated: function () {
            this.AIRegenerateInterval && clearInterval(this.AIRegenerateInterval);
            this.clearLocalAiTimer();
            this.clearPartsDeadline();
            this.dropUrlImproveGate();
            this.debouncedMergeText.cancel();
            clearAiContentWatchers();
            document.removeEventListener("copy", this.copyListener);
            EventBus.$off('save');
            EventBus.$off('request-enhance', this.manualEnhance);
            EventBus.$off('generation-applied', this.onGenerationApplied);
            EventBus.$off('proposals-ready', this.onProposalsReady);
            EventBus.$off('parts-generation-failed', this.onPartsGenerationFailed);
            if (this.polishingToastId) {
                this.$store.dispatch('toasts/remove', this.polishingToastId);
            }
            if (this.generatingToastId) {
                this.$store.dispatch('toasts/remove', this.generatingToastId);
            }
            if (this.enhancingToastId) {
                this.$store.dispatch('toasts/remove', this.enhancingToastId);
            }
        },
        methods: {
            syncInflectedKeywords: function (): void {
                const main = resolveArticleMainKeyword(this.article).trim();
                if (!main || !this.article) {
                    return;
                }
                // Never rebuild the density list while a left-menu row is focused.
                // Keystroke → $set(allKeywords) → this watcher was the freeze.
                if (isKeywordDensityEditing()) {
                    return;
                }
                const nextKeywords = syncKeywordDensityList(
                    this.article.allKeywords || [],
                    main,
                    this.article.language_code,
                    this.article.secondaryKeywords || []
                );
                if (nextKeywords !== this.article.allKeywords
                    && !densityListsEqual(this.article.allKeywords, nextKeywords)) {
                    this.$set(this.article, 'allKeywords', nextKeywords);
                }
            },
            importEatAttribution: function (scraped?: ScrapperResponse): Promise<void> {
                const url = this.article.url;
                if (!this.isAnalyze || !url) {
                    return Promise.resolve();
                }
                if (hasEatAuthor(this.article.eatAttribution) && !scraped) {
                    return Promise.resolve();
                }
                this.eatLoading = true;
                return loadEatAttributionForUrl(url, scraped)
                    .then((eatAttribution) => {
                        if (hasEatAuthor(eatAttribution)) {
                            this.$store.commit('setArticleEatAttribution', eatAttribution);
                        }
                    })
                    .catch(() => undefined)
                    .then(() => {
                        this.eatLoading = false;
                    });
            },
            mergeText: function (): void {
                let mergedText = '';
                for (const {value: part} of this.article.getAllParts()) {
                    mergedText += part.reduce((acc: string, subpart: ArticlePartContent) => `${acc} | ${subpart.h} | ${subpart.p} `, '');
                }
                mergedText += this.article.questions.reduce((acc: string, question: ArticleQuestion) => `${acc} | ${question.question} | ${question.answer} `, '');
                this.mergedText = stripTagsAndEntities(mergedText);
                // mergeText itself is trailing-debounced for body typing.
                this.countWordOccurrences();
            },
            updateWordsToHighlight: function (): void {
                const next: { [key: string]: string[] } = {};
                for (const keyword of this.validKeywords) {
                    next[keyword] = [keyword];
                }
                const prevKeys = Object.keys(this.wordsToHighlight);
                const nextKeys = Object.keys(next);
                const unchanged = prevKeys.length === nextKeys.length
                    && prevKeys.every((key) => nextKeys.includes(key));
                if (unchanged) {
                    return;
                }
                this.wordsToHighlight = next;
            },
            scrapUrls: function (urls: string[]): void {
                this.loadingInspirations = true;
                const promises = [];
                for (const url of urls) {
                    promises.push(Functions.GetScrapped({site: url})
                        .then(response => {
                            if (response.status !== 200) {
                                window.console.warn("Failed to scrap " + response.site);
                            } else {
                                this.$set(this.inspirations, encodeURIComponent(response.site), response);
                            }
                        })
                        .catch(error => {
                            // Do not show scrap error to the user
                            // this.$store.dispatch("handleApiError", error);
                            window.console.warn(error);
                        }));
                }
                Promise.all(promises)
                    .catch(error => {
                        this.$store.dispatch('toasts/add', {
                            type: 'error',
                            title: 'inspirationsError.title',
                        } as Toast);
                        captureException(error);
                    })
                    .finally(() => {
                        this.loadingInspirations = false;
                        this.syncCoverageFromInspirations();
                    });
            },
            syncCoverageFromInspirations: function (): void {
                const scrapes = Object.values(this.inspirations || {}).filter((item) => item && item.status === 200);
                if (!scrapes.length) {
                    return;
                }
                const currentHeadings = [
                    ...(this.article.h2 || []).map((part) => part.h),
                    ...(this.article.h3 || []).map((part) => part.h),
                ];
                const next = applyScrapedCoverage(this.article.aiWritingBrief, scrapes, currentHeadings);
                this.$set(this.article, 'aiWritingBrief', next);
            },
            countWordOccurrences: function (): void {
                const language = this.article.language_code || 'cs';
                const phrases = Object.keys(this.wordsToHighlight);
                const counts = countStemKeywordHitsForPhrases(this.mergedText, phrases, language);
                for (const word of phrases) {
                    this.$store.commit('setWordOccurrences', [
                        word,
                        counts[word] || 0,
                    ]);
                }
            },
            saveArticle: function (callback: (() => void) | null = null): void {
                this.$emit('save', () => {
                    if (typeof callback === 'function') {
                        callback();
                    }
                });
            },
            shouldConfirmGeneration: function (): boolean {
                if (this.sharePreview || !this.article || !this.article.id) {
                    return false;
                }
                if (this.$store.getters.articleAiFailed || this.article.aiGenerateStatus === 'failed') {
                    return false;
                }
                if (this.generationUiFinished) {
                    return false;
                }
                if (this.generatingToastId || this.enhancingToastId) {
                    return true;
                }
                if (this.article.enhanced === 'processing' || this.article.aiGenerateStatus === 'running') {
                    return true;
                }
                return this.isNew && !this.isAnalyze;
            },
            chooseProposal: function (id: string, action: ProposalChoice): void {
                if (!this.article || !this.isAnalyze) {
                    return;
                }
                const result = applyBlockProposalChoice(this.article, id, action);
                if (!result.changed) {
                    return;
                }
                this.$set(this.article, 'blockProposals', result.article.blockProposals);
                (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as ArticleContentKey[]).forEach((part) => {
                    if (result.article[part]) {
                        this.$set(this.article, part, result.article[part]);
                    }
                });
                if (result.article.questions) {
                    this.$set(this.article, 'questions', result.article.questions);
                }
                const status = settledGenerationStatus(result.article.blockProposals);
                if (status) {
                    this.generationUiFinished = true;
                    this.$set(this.article, 'enhanced', status.enhanced);
                    this.$set(this.article, 'aiGenerateStatus', status.aiGenerateStatus);
                    this.$store.dispatch('toasts/add', {
                        type: 'success',
                        title: 'article.blockProposals.settled',
                        dismissible: true,
                        timeout: 8000,
                    } as Toast);
                }
                this.saveArticle();
            },
            onProposalsReady: function (): void {
                this.enhancing = false;
                this.clearPartsDeadline();
                this.localAiUnlocked = true;
                const failure = hollowReviewFailure(this.article);
                if (!this.article || !this.article.blockProposals || !this.article.blockProposals.length || failure) {
                    if (failure && this.article) {
                        this.$set(this.article, 'enhanced', failure.enhanced);
                        this.$set(this.article, 'aiGenerateStatus', failure.aiGenerateStatus);
                        void Firestore.updateArticleGenerationStatus(this.article, failure);
                    }
                    this.onPartsGenerationFailed();
                    return;
                }
                const toast = {
                    type: 'info',
                    title: 'article.blockProposals.readyTitle',
                    content: 'article.blockProposals.readyContent',
                    dismissible: true,
                    timeout: 8000,
                } as Toast;
                if (this.enhancingToastId) {
                    this.$store.dispatch('toasts/replace', {
                        ...toast,
                        id: this.enhancingToastId,
                    });
                    this.enhancingToastId = null;
                    return;
                }
                this.$store.dispatch('toasts/add', toast);
            },
            onGenerationApplied: function (): void {
                if (this.shouldConfirmGeneration()) {
                    void this.confirmGenerationSave();
                }
            },
            confirmGenerationSave: function (): Promise<void> {
                if (this.article?.enhanced === 'done' || this.article?.aiGenerateStatus === 'done') {
                    this.finishGenerationUi();
                    this.addUrlsToArticle(true, false);
                }
                return Promise.resolve();
            },
            finishGenerationUi: function (): void {
                if (this.generationUiFinished) {
                    return;
                }
                const held = (this.$store.state.toasts && this.$store.state.toasts.toasts) || [];
                dismissSaveHeldToasts(held, (id: number) => {
                    this.$store.dispatch('toasts/remove', id);
                });
                this.generationUiFinished = true;
                this.enhancing = false;
                this.articleEnhancePossible = false;
                this.localAiUnlocked = true;
                this.clearLocalAiTimer();
                this.clearPartsDeadline();
                if (this.generatingToastId) {
                    this.$store.dispatch('toasts/replace', {
                        id: this.generatingToastId,
                        type: 'success',
                        title: 'article.generated.title',
                    } as Toast);
                    this.generatingToastId = null;
                }
                if (this.enhancingToastId) {
                    this.$store.dispatch('toasts/replace', {
                        id: this.enhancingToastId,
                        type: 'success',
                        title: 'article.enhanced.title',
                    } as Toast);
                    this.enhancingToastId = null;
                }
                this.$store.dispatch('toasts/add', {
                    type: 'success',
                    title: 'article.saved.title',
                } as Toast);
                this.$store.dispatch('setArticleLastSavedSnapshotHash');
            },
            copyListener: function (event: ClipboardEvent): void {
                if (!event.clipboardData || !window.getSelection()) {
                    return;
                }
                const helper = document.createElement("div");
                const selection = window.getSelection();
                if (selection !== null && selection.type !== 'None') {
                    helper.appendChild(selection.getRangeAt(0).cloneContents());
                    event.clipboardData.setData("text/html", stripMarkers(helper.innerHTML));
                    event.clipboardData.setData("text/plain", helper.innerText);
                }
            },
            clearPartsDeadline: function (): void {
                if (this.partsDeadlineTimer) {
                    clearTimeout(this.partsDeadlineTimer);
                    this.partsDeadlineTimer = null;
                }
            },
            armPartsDeadline: function (waitMs: number, after: 'parts' | 'pending' = 'parts'): void {
                this.clearPartsDeadline();
                this.partsDeadlineTimer = setTimeout(() => {
                    this.partsDeadlineTimer = null;
                    if (after === 'pending') {
                        this.syncPendingUrlImprove();
                        return;
                    }
                    void this.refreshPartsTerminal(false);
                }, waitMs);
            },
            refreshPartsTerminal: function (startWatchers: boolean): Promise<void> {
                return settleProcessingParts(this.article).then((decision) => {
                    if (decision.action === 'success') {
                        rearmGenerationStatusWatch();
                        void this.confirmGenerationSave();
                        return;
                    }
                    if (decision.action === 'failed') {
                        this.onPartsGenerationFailed();
                        return;
                    }
                    if (!startWatchers && decision.action !== 'wait') {
                        return;
                    }
                    if (decision.action === 'wait') {
                        this.armPartsDeadline(decision.waitMs || PARTS_GENERATE_DEADLINE_MS);
                    }
                    if (startWatchers && this.article.enhanced === 'processing') {
                        this.enhancing = true;
                        this.addEnhancingToast();
                        watchForAi();
                    }
                });
            },
            /**
             * After a failed run the page offers Zkusit znovu only, when it shows it.
             * Vylepšit článek comes back with a done article (keyword workspace).
             */
            offerEnhanceAfterFailure: function (): void {
                const retryShown = !!this.article
                    && (this.isAnalyze || this.article.aiGenerateMode === 'whole')
                    && partsRetryVisible(this.article);
                this.articleEnhancePossible = !retryShown;
            },
            onPartsGenerationFailed: function (): void {
                this.enhancing = false;
                this.clearPartsDeadline();
                this.offerEnhanceAfterFailure();
                const toast = {
                    type: 'error',
                    title: 'article.partsFailed.title',
                    content: 'article.partsFailed.content',
                    dismissible: true,
                } as Toast;
                if (this.enhancingToastId) {
                    this.$store.dispatch('toasts/replace', {
                        ...toast,
                        id: this.enhancingToastId,
                    });
                    this.enhancingToastId = null;
                    return;
                }
                this.$store.dispatch('toasts/add', toast);
            },
            retryFailedProposal: function (id: string): Promise<void> {
                if (!this.article || !this.isAnalyze) {
                    return Promise.resolve();
                }
                const article = this.article;
                // Taken before retryFailedBlock writes running. A start that
                // fails ran nothing: the article goes back to its review, not
                // to failed with the whole-article Retry.
                const before = {enhanced: article.enhanced, aiGenerateStatus: article.aiGenerateStatus};
                return startGenerationRun(article, () => retryFailedBlock(article, id), captureException, before).then((outcome) => {
                    if (outcome === 'failed') {
                        return;
                    }
                    this.generationUiFinished = false;
                    this.enhancing = true;
                    this.localAiUnlocked = false;
                    this.addEnhancingToast();
                    rearmGenerationStatusWatch();
                    watchForAi();
                    this.armPartsDeadline(PARTS_GENERATE_DEADLINE_MS);
                });
            },
            onPartsRetry: function (): Promise<void> {
                const article = this.article;
                return startGenerationRun(article, () => beginPartsRetry(article), captureException).then((outcome) => {
                    if (outcome === 'failed') {
                        return;
                    }
                    this.generationUiFinished = false;
                    this.enhancing = true;
                    this.localAiUnlocked = false;
                    this.addEnhancingToast();
                    watchForAi();
                    this.armPartsDeadline(PARTS_GENERATE_DEADLINE_MS);
                });
            },
            dropUrlImproveGate: function (): void {
                if (this.unsubscribeUrlImproveGate) {
                    this.unsubscribeUrlImproveGate();
                    this.unsubscribeUrlImproveGate = null;
                }
            },
            failStuckPending: function (): void {
                // Status null and enhanced pending are not a running write.
                // Opening the article must not set failed.
            },
            applyUrlImproveEnqueue: function (): void {
                if (this.sharePreview || !this.isAnalyze || !this.article || this.article.enhanced !== 'pending') {
                    return;
                }
                if (this.article.aiGenerateStatus === 'failed' || this.article.aiGenerateStatus === 'done') {
                    return;
                }
                const gate = readUrlImproveGate();
                const routeChoice = this.$route && this.$route.query ? this.$route.query.cesta : null;
                const decision = decideUrlImproveEnqueue({
                    isAnalyze: true,
                    enhanced: this.article.enhanced,
                    aiGenerateStatus: this.article.aiGenerateStatus,
                    aiGenerateMode: this.article.aiGenerateMode,
                    customizeFirst: gate.customizeFirst,
                    analysisLoading: gate.analysisLoading,
                    briefLoading: gate.briefLoading,
                    scrapeLoading: !!this.$store.getters.articleAnalyzeScrapeLoading,
                    scrapedHeadingsSaved: articleHasScrapedHeadings(this.article),
                    allowAutoStart: !!this.hasPlan,
                    immediateRoute: readChatWritePath(routeChoice) === 'immediate',
                    now: Date.now(),
                    waitingSince: this.article.created_at,
                });
                if (decision.action === 'enqueue') {
                    // Guard: the watcher must not start writing. cesta=immediate
                    // for URL-improve waits for Vylepšit článek / Generovat SEO článek.
                    return;
                }
                if (decision.action === 'failed') {
                    this.failStuckPending();
                    return;
                }
                if (decision.action === 'wait') {
                    this.armPartsDeadline(decision.waitMs, 'pending');
                }
            },
            syncPendingUrlImprove: function (): void {
                if (this.localAiUnlocked || this.sharePreview || !this.isAnalyze || !this.article?.id || this.article.enhanced !== 'pending') {
                    return;
                }
                if (this.article.aiGenerateStatus === 'failed' || this.article.aiGenerateStatus === 'done') {
                    return;
                }
                void settleProcessingParts(this.article).then((decision) => {
                    if (!this.article || this.article.enhanced !== 'pending') {
                        return;
                    }
                    if (this.article.aiGenerateStatus === 'failed' || this.article.aiGenerateStatus === 'done') {
                        this.enhancing = false;
                        this.clearPartsDeadline();
                        return;
                    }
                    if (decision.action === 'success') {
                        rearmGenerationStatusWatch();
                        void this.confirmGenerationSave();
                        return;
                    }
                    if (decision.action === 'failed') {
                        this.enhancing = false;
                        this.clearPartsDeadline();
                        return;
                    }
                    if (decision.action === 'wait') {
                        this.armPartsDeadline(decision.waitMs || PARTS_GENERATE_DEADLINE_MS);
                        return;
                    }
                    this.applyUrlImproveEnqueue();
                });
            },
            reGenerateAiContent: function () {
                if (this.article.id && this.$store.getters.articleAiLoading.loadingRatioTotal > 0) {
                    window.console.info('Re-generating AI content after 10 minutes');
                    Functions.OpenAIArticleReGenerate({
                        articleId: this.article.id
                    });
                    Firestore.updateArticleAIGeneratedAt(this.article);
                }
            },
            addGeneratingToast: function () {
                if (this.generatingToastId) {
                    return;
                }
                const local = isLocalPreviewAi();
                this.$store.dispatch('toasts/add', {
                    type: 'info',
                    title: 'article.generating.title',
                    content: local ? 'article.generating.localContent' : 'article.generating.content',
                    dismissible: local,
                    action: local ? {
                        text: 'article.generating.continueWithoutAi',
                        callback: () => this.releaseAiUiLock('generate', 'local')
                    } : undefined,
                } as Toast)
                    .then((toastId: number) => {
                        if (this.generationUiFinished) {
                            this.$store.dispatch('toasts/replace', {
                                id: toastId,
                                type: 'success',
                                title: 'article.generated.title',
                            } as Toast);
                            return;
                        }
                        this.generatingToastId = toastId;
                    });
            },
            addEnhancingToast: function () {
                if (this.enhancingToastId) {
                    return;
                }
                const local = isLocalPreviewAi();
                this.$store.dispatch('toasts/add', {
                    type: 'info',
                    title: 'article.enhancing.title',
                    content: local ? 'article.enhancing.localContent' : 'article.enhancing.content',
                    dismissible: local,
                    action: local ? {
                        text: 'article.enhancing.continueWithoutAi',
                        callback: () => this.releaseAiUiLock('enhance', 'local')
                    } : undefined,
                } as Toast)
                    .then((toastId: number) => {
                        if (this.generationUiFinished) {
                            this.$store.dispatch('toasts/replace', {
                                id: toastId,
                                type: 'success',
                                title: 'article.enhanced.title',
                            } as Toast);
                            return;
                        }
                        this.enhancingToastId = toastId;
                    });
            },
            clearLocalAiTimer: function (): void {
                if (this.localAiTimer) {
                    clearTimeout(this.localAiTimer);
                    this.localAiTimer = null;
                }
            },
            armLocalAiTimeout: function (kind: 'enhance' | 'generate'): void {
                this.clearLocalAiTimer();
                if (!isLocalPreviewAi()) {
                    return;
                }
                this.localAiTimer = setTimeout(() => {
                    this.releaseAiUiLock(kind, 'local');
                }, 16_000);
            },
            releaseAiUiLock: function (kind: 'enhance' | 'generate', reason: 'local' | 'error'): void {
                if (this.localAiUnlocked) {
                    return;
                }
                this.localAiUnlocked = true;
                this.clearLocalAiTimer();
                this.preparingAiLocal = false;
                this.enhancing = false;
                this.polishing = false;
                unlockLocalArticleEditors();
                if (kind === 'enhance') {
                    this.$set(this.article, 'enhanced', 'pending');
                    if (reason === 'error') {
                        this.offerEnhanceAfterFailure();
                    } else {
                        this.articleEnhancePossible = true;
                    }
                    if (this.enhancingToastId) {
                        this.$store.dispatch('toasts/replace', {
                            id: this.enhancingToastId,
                            type: reason === 'error' ? 'error' : 'info',
                            title: reason === 'error' ? 'article.enhanceError.title' : 'article.enhancing.localDoneTitle',
                            content: reason === 'error' ? 'article.enhanceError.content' : 'article.enhancing.localDoneContent',
                            dismissible: true,
                            timeout: 8000,
                        } as Toast);
                    }
                    this.enhancingToastId = null;
                }
                if (kind === 'generate' && this.generatingToastId) {
                    this.$store.dispatch('toasts/replace', {
                        id: this.generatingToastId,
                        type: 'info',
                        title: 'article.generating.localDoneTitle',
                        content: 'article.generating.localDoneContent',
                        dismissible: true,
                        timeout: 8000,
                    } as Toast);
                    this.generatingToastId = null;
                }
            },
            addUrlsToArticle: function (forced: boolean = false, fromConfirm: boolean = false) {
                if ((this.isNew && this.articleContentReady && !this.articleUrlsAdded && !this.loadingSERP) || forced) {
                    // new article - content is ready (generated or scraped), SERP links are loaded and urls were not added yet
                    // add first SERP URL to the first article paragraph
                    if (this.SERPLinks.length > 0 && this.SERPLinks[0].url && this.article.h1[0] && this.article.h1[0].p) {
                        const firstUrl = this.SERPLinks[0].url;
                        const contentPart = this.article.h1[0].p;
                        if (!contentPart.includes(firstUrl)) {
                            this.placeURLIntoParagraph(contentPart, firstUrl, 'h1', 0, 'p');
                        }
                    } else if (!this.isAnalyze) {
                        console.warn('No SERP links available to add to the article.');
                    }
                    // add suggested URL into the first H2 paragraph. Never invent or
                    // duplicate a heading when that paragraph is not on the page yet.
                    // Both content-ready and SERP-loaded used to warn while h2[0].p
                    // was still empty. That did not create the empty heading slots.
                    const paragraph = this.article.h2[0] && this.article.h2[0].p;
                    const urlAction = suggestedH2UrlAction(paragraph, !!(forced || this.article.enhanced === 'done'));
                    if (urlAction === 'wait') {
                        return;
                    }
                    if (urlAction === 'warn') {
                        if (!this.suggestedUrlWarned) {
                            console.warn('Unable to add suggested URL into h2.');
                            this.suggestedUrlWarned = true;
                        }
                        return;
                    }
                    const url = this.$store.getters.article.url;
                    const baseUrl = url !== '' ? getBaseUrl(url) : FALLBACK_URL;
                    const suggestedUrl = getSuggestedUrl(baseUrl, this.article.selectedKeyword);
                    const contentPart = this.article.h2[0].p;
                    if (!contentPart.includes(suggestedUrl)) {
                        this.placeURLIntoParagraph(contentPart, suggestedUrl, 'h2', 0, 'p');
                    }
                    this.articleUrlsAdded = true;
                    if (fromConfirm) {
                        return;
                    }
                    if (this.shouldConfirmGeneration()) {
                        void this.confirmGenerationSave();
                        return;
                    }
                    this.$emit('save');
                }
            },
            placeURLIntoParagraph: function (content: string, url: string, contentPart: ArticleContentKey, index: number, contentKey: ArticleContentTag) {
                // Place the URL onto the last inflected stem hit (barf → barfu), not an exact string.
                const language = this.article.language_code || 'cs';
                let lastRange: {start: number, end: number} | null = null;
                for (const keyword of Object.keys(this.wordsToHighlight)) {
                    for (const range of findStemTokenRanges(content, keyword, language)) {
                        if (!lastRange || range.start >= lastRange.start) {
                            lastRange = range;
                        }
                    }
                }
                let placed = false;
                if (lastRange) {
                    const lastMatch = content.slice(lastRange.start, lastRange.end);
                    const beforeMatch = content.substring(0, lastRange.start);
                    const afterMatch = content.substring(lastRange.end);
                    content = beforeMatch + `<a href="${url}" target="_blank">${lastMatch}</a>` + afterMatch;
                    placed = true;
                }
                if (!placed) {
                    // If no keyword was found, just append the URL to the content
                    content += `<a href="${url}" target="_blank">${this.article.selectedKeyword}</a>`;
                }
                this.$store.commit('setArticleContent', {
                    contentPart: contentPart,
                    index: index,
                    contentKey: contentKey,
                    value: content
                });
            },
            pruneIncompleteFaqsBeforeGenerate: function (): void {
                const rows = this.article.questions || [];
                const incomplete = countIncompleteFaqRows(rows);
                if (!incomplete) {
                    return;
                }
                const kept = rows.filter((item) => isCompleteFaqPair(item.question, item.answer));
                this.$store.commit('replaceArticleQuestions', kept);
                this.$store.dispatch('toasts/add', {
                    type: 'warning',
                    title: 'article.faqIncompletePruned.title',
                    content: 'article.faqIncompletePruned.content',
                } as Toast);
            },
            enhanceArticle: function () {
                if (!this.article.id) {
                    this.releaseAiUiLock('enhance', 'error');
                    return;
                }
                this.pruneIncompleteFaqsBeforeGenerate();
                this.armLocalAiTimeout('enhance');
                const article = this.article;
                const request = clientEnhanceRequest(article);
                // manualEnhance already wrote running. A start that fails ends
                // failed with Retry, a 409 follows the run already going, the
                // same as the article page (#30937). Local Preview has no
                // functions and only unlocks.
                const start = isLocalPreviewAi()
                    ? Functions.OpenAIArticleGenerate(request).then((): GenerationRunStart => 'started')
                    : startGenerationRun(article, () => Functions.OpenAIArticleGenerate(request), captureException);
                const generate = start.then<void[] | 'failed'>((outcome) => {
                    if (outcome === 'failed') {
                        this.releaseAiUiLock('enhance', 'error');
                        return outcome;
                    }
                    if (outcome === 'following') {
                        // Another tab's run is in progress: take its clock and follow it.
                        return adoptRunInProgress(article).then(() => watchForAi());
                    }
                    return watchForAi();
                });
                raceWithLocalAiTimeout(generate)
                    .then((result) => {
                        if (result === 'timeout') {
                            this.releaseAiUiLock('enhance', 'local');
                            return;
                        }
                        if (isLocalPreviewAi() && this.loadingCompleted) {
                            this.releaseAiUiLock('enhance', 'local');
                        }
                    })
                    .catch((error) => {
                        if (generationAlreadyRunning(error)) {
                            // Another tab's run is in progress: keep the editors locked and follow it.
                            return adoptRunInProgress(this.article).then(() => watchForAi());
                        }
                        this.releaseAiUiLock('enhance', isLocalPreviewAi() ? 'local' : 'error');
                        captureException(error);
                    });
            },
            manualEnhance: function () {
                if (enhanceWaitingForExtraction({
                    isAnalyze: !!this.isAnalyze,
                    scrapeLoading: !!this.$store.getters.articleAnalyzeScrapeLoading,
                })) {
                    return;
                }
                collapseAnalysisAndScrollToHBlocks();
                if (this.article && this.article.enhanced === 'processing') {
                    return;
                }
                if (!this.planLoaded) {
                    runWhenPlanLoaded(this, () => this.manualEnhance());
                    return;
                }
                // Check if user has a paid plan for AI enhance feature
                if (!this.hasPlan) {
                    this.$store.dispatch('toasts/add', {
                        type: 'error',
                        title: 'subscriptions.paywall.enhanceRequiresPlan.title',
                        content: 'subscriptions.paywall.enhanceRequiresPlan.content',
                        action: {
                            text: 'subscriptions.paywall.enhanceRequiresPlan.action',
                            callback: () => this.$router.push('/account')
                        }
                    });
                    return;
                }

                this.generationUiFinished = false;
                this.enhancing = true;
                this.localAiUnlocked = false;
                this.clearPartsDeadline();
                this.$store.commit('setArticleAiFailed', false);
                this.$set(this.article, 'enhanced', 'processing');
                this.$set(this.article, 'aiGenerateStatus', 'running');
                this.$set(this.article, 'ai_content_last_generated_at', Date.now());
                syncSelectedBriefIntoKeywords(this.article);
                EventBus.$emit('update-article-keywords-for-ai');
                // Running and the start timestamp land before the content save
                // and before OpenAIArticleGenerate. A leftover failed status
                // must not be what the save or the status watcher sees.
                void markPartsRunStarted(this.article).then(() => {
                    EventBus.$emit("onArticleSave", () => {
                        this.addEnhancingToast();
                        this.enhanceArticle();
                    }, {generationStart: true});
                });
            }
        }
    });
// TODO refactor
</script>

<style scoped lang="sass">
#articleCreationContainer
    scroll-margin-top: 88px

.scrollable
    display: flex !important
    flex-direction: column

.scrollable-content
    flex-grow: 1
    overflow: auto

.animated
    animation: flash .5s linear infinite

.no-drawer
    width: 100vw !important

@keyframes flash
    50%
        background: rgba(255, 255, 255, 0.5)
</style>

<style lang="sass">
.ck-toolbar-container
    z-index: 1 !important

.inspirationsLoader .v-progress-linear__determinate
    background: linear-gradient(90deg, var(--v-primary-lighten1) 0%, var(--v-primary-darken2) 100%)

.v-application .aiLoader
    .v-progress-linear__determinate
        background: linear-gradient(90deg, var(--v-ai-base) 0%, var(--v-ai-darken4) 100%)

    .v-progress-linear__stream
        color: var(--v-ai-base) !important
        caret-color: var(--v-ai-base) !important

.workspace-inspiration-actions
    display: flex
    flex-wrap: wrap
    align-items: stretch
    gap: 16px
    margin-top: 16px
    margin-bottom: 175px

.editorsBuffer
    padding-bottom: 420px
</style>
